import { PlatformCardInsights, AllPlatformsQuery } from '@/__generated__/graphql';

/**
 * Extended interface for insights with filter options
 */
export interface InsightWithFilterOptions extends PlatformCardInsights {
    filterOptions?: string[];
}

/**
 * Configuration for risk calculations
 */
export interface RiskCalculationConfig {
    selectedDeploymentTypes: string[];
    categoryDataTypes: Record<string, string[]>;
}

/**
 * Type for platform data from GraphQL
 */
type PlatformData = AllPlatformsQuery['allPlatforms'][0];

/**
 * Step 1: Determine the category for an insight based on its filterOptions
 *
 * @param insight - The insight to categorize
 * @returns The category name (lowercased) or 'uncategorized' if no filterOptions
 */
const getInsightCategory = (insight: { filterOptions?: string[] }): string => {
    if (insight.filterOptions && insight.filterOptions.length > 0 && insight.filterOptions[0]) {
        return insight.filterOptions[0].toLowerCase();
    }
    return 'uncategorized';
};

/**
 * Step 2: Check if an insight should be included in the calculation
 *
 * @param insight - The insight to check
 * @param config - Configuration containing selected categories and data types
 * @returns true if the insight should be included, false if it should be subtracted
 */
const shouldIncludeInsight = (
    insight: InsightWithFilterOptions,
    config: RiskCalculationConfig
): boolean => {
    const { selectedDeploymentTypes, categoryDataTypes } = config;

    // Step 2a: Determine which category this insight belongs to
    const insightCategory = getInsightCategory(insight);

    // Step 2b: Check if this insight's category is selected
    if (!selectedDeploymentTypes.includes(insightCategory)) {
        return false; // Category not selected, exclude this insight
    }

    // Step 2c: Category is selected, check if this specific insight type is enabled for this category
    const categoryEnabledTypes = categoryDataTypes[insightCategory] || [];
    const insightType = insight.name.toLowerCase();

    if (!categoryEnabledTypes.includes(insightType)) {
        return false; // Insight type not enabled for this category, exclude this insight
    }

    return true; // Insight should be included in the calculation
};

/**
 * Step 3: Calculate the consolidated value for a group of insights
 *
 * This function implements the "sum all, then subtract unchecked" approach:
 * 1. Sum all insight values
 * 2. Subtract values from insights that should be excluded
 * 3. Return the final calculated value
 *
 * @param insights - Array of insights to consolidate
 * @param config - Configuration for the calculation
 * @returns The consolidated numeric value
 */
const calculateConsolidatedValue = (
    insights: InsightWithFilterOptions[],
    config: RiskCalculationConfig
): number => {
    // Step 3a: Sum all values first (this gives us the total)
    const allValuesSum = insights.reduce((sum, insight) => {
        const value = parseFloat(insight.value?.replace(/,/g, '') || '0');
        return sum + value;
    }, 0);

    // Step 3b: Calculate values to subtract (unchecked categories/sub-filters)
    const uncheckedValuesSum = insights.reduce((sum, insight) => {
        const insightValue = parseFloat(insight.value?.replace(/,/g, '') || '0');

        // If this insight should be excluded, add its value to the subtraction total
        if (!shouldIncludeInsight(insight, config)) {
            return sum + insightValue;
        }

        return sum; // Insight is included, don't subtract its value
    }, 0);

    // Step 3c: Final value = all values - unchecked values
    return allValuesSum - uncheckedValuesSum;
};

/**
 * Step 4: Consolidate insights by name and calculate values based on selected categories and sub-filters
 *
 * This function groups insights by name (e.g., "POI risk", "Area risk") and calculates
 * consolidated values for each group based on the current filter selections.
 *
 * @param insights - Array of insights to consolidate
 * @param config - Configuration for the calculation
 * @returns Array of consolidated insights with calculated values
 */
export const consolidateInsightsByCategory = (
    insights: InsightWithFilterOptions[],
    config: RiskCalculationConfig
): InsightWithFilterOptions[] => {
    // Step 4a: Group insights by name (POI risk, Area risk, etc.)
    const nameGroups: Record<string, InsightWithFilterOptions[]> = {};

    insights.forEach((insight) => {
        if (!nameGroups[insight.name]) {
            nameGroups[insight.name] = [];
        }
        nameGroups[insight.name].push(insight);
    });

    // Step 4b: Calculate consolidated values for each name group
    return Object.entries(nameGroups).map(([, groupInsights]) => {
        const totalValue = calculateConsolidatedValue(groupInsights, config);

        // Step 4c: Use the first insight as template and update the value
        return {
            ...groupInsights[0],
            value: totalValue.toFixed(2),
        };
    });
};

/**
 * Step 5: Check if a platform matches the selected deployment types (categories)
 *
 * This function determines if a platform should be visible based on whether
 * it has insights that match the currently selected categories.
 *
 * @param platform - The platform to check
 * @param selectedDeploymentTypes - Array of selected category names
 * @returns true if the platform should be visible, false otherwise
 */
export const platformMatchesDeploymentTypes = (
    platform: PlatformData,
    selectedDeploymentTypes: string[]
): boolean => {
    const insights = platform?.platformCardInsights ?? [];
    // Include platforms without platformCardInsights
    if (insights.length === 0) {
        return true;
    }

    return insights.some((insight) => {
        // Step 5a: Only check insights in "Risk score" group
        if (insight.group.toLowerCase() !== 'risk score') return false;

        // Step 5b: Check if platform has filterOptions
        const hasFilterOptions = (insight.filterOptions?.length ?? 0) > 0;

        // Step 5c: If platform has filterOptions, check if any match selected categories
        if (hasFilterOptions) {
            return (
                insight.filterOptions?.some(
                    (filterOption: string | null) =>
                        filterOption && selectedDeploymentTypes.includes(filterOption.toLowerCase())
                ) || false
            );
        }

        // Step 5d: If platform has no filterOptions, it's uncategorized
        return selectedDeploymentTypes.includes('uncategorized');
    });
};

/**
 * Step 6: Extract available categories and sub-filters from platform insights
 *
 * This function analyzes all platform insights to determine:
 * - What categories are available
 * - What sub-filter types exist for each category
 * - The mapping between insight types and their display names
 *
 * @param platforms - Array of platforms to analyze
 * @returns Object containing categories, sub-filters, and insight type mapping
 */
export const extractAvailableCategories = (platforms: PlatformData[]) => {
    const categories = new Set<string>();
    const categorySubFilters: Record<string, Set<string>> = {};
    const insightTypeMap: Record<string, string> = {};

    platforms.forEach((platform) => {
        platform.platformCardInsights?.forEach((insight) => {
            // Step 6a: Only process insights that belong to "Risk score" group
            if (insight.group.toLowerCase() !== 'risk score') return;

            const filterOptions = insight.filterOptions || [];
            const insightKey = insight.name.toLowerCase();

            // Step 6b: Store insight name mapping (lowercased key -> display name)
            insightTypeMap[insightKey] = insight.name;

            if (filterOptions.length > 0) {
                // Step 6c: Process insights with filterOptions
                filterOptions.forEach((filterOption: string | null) => {
                    if (filterOption) {
                        categories.add(filterOption);

                        // Step 6d: Add this specific insight name as a sub-filter for this category
                        if (!categorySubFilters[filterOption]) {
                            categorySubFilters[filterOption] = new Set();
                        }
                        categorySubFilters[filterOption].add(insightKey);
                    }
                });
            } else {
                // Step 6e: Process uncategorized insights
                categories.add('Uncategorized');

                if (!categorySubFilters['Uncategorized']) {
                    categorySubFilters['Uncategorized'] = new Set();
                }
                categorySubFilters['Uncategorized'].add(insightKey);
            }
        });
    });

    return {
        categories: Array.from(categories).sort(),
        subFilters: categorySubFilters,
        insightTypeMap,
    };
};
